import { useState } from "react";
import { CursorTable, type CursorTableColumn } from "@core/table";
import { tokens } from "@core/theme/tokens";
import type { components } from "@core/types/openapi";
import {
  AdvancedFiltersModal,
  EmptyState,
  securityLogFilterBarStyles,
  SecurityHeader,
  SecurityLogFilterBar,
  Tooltip,
} from "@portal/common";
import type { AccessLogFilters } from "./api";
import { useAccessLog } from "./accessLogQueries";
import { accessLogStyles } from "./accessLogStyles";

type AccessLogEntry = components["schemas"]["AccessLogEntry"];
type ResultFilter = "all" | NonNullable<AccessLogFilters["result"]>;
type OriginFilter = "all" | NonNullable<AccessLogFilters["origin"]>;

const reasonLabel: Record<NonNullable<AccessLogEntry["reason"]>, string> = {
  incorrect_password: "Contraseña incorrecta",
  user_inactive: "Usuario inactivo",
  invalid_credentials: "Credenciales inválidas",
  account_locked: "Cuenta bloqueada",
};

const accessLogDateTimeFormatter = new Intl.DateTimeFormat("es-EC", {
  timeZone: "America/Guayaquil",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

function formatInstant(value: string) {
  const parts = accessLogDateTimeFormatter.formatToParts(new Date(value));

  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return `${getPart("day")} ${getPart("month")} ${getPart("year")} · ${getPart("hour")}:${getPart("minute")}`;
}

function formatDate(value: string) {
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function localIsoDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function defaultDateRange() {
  const to = new Date();
  const from = new Date(to);
  from.setDate(to.getDate() - 6);
  return { from: localIsoDate(from), to: localIsoDate(to) };
}

export function AccessLogScreen() {
  const [defaultRange] = useState(defaultDateRange);
  const [result, setResult] = useState<ResultFilter>("all");
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [origin, setOrigin] = useState<OriginFilter>("all");
  const [staffUserId, setStaffUserId] = useState("");
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const filters: AccessLogFilters = {
    ...(result === "all" ? {} : { result }),
    ...(origin === "all" ? {} : { origin }),
    ...(staffUserId.trim() ? { staffUserId: staffUserId.trim() } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
  const accessLog = useAccessLog(filters);
  const rows = accessLog.data?.pages.flatMap((page) => page.items) ?? [];
  const lastPage = accessLog.data?.pages.at(-1);
  const availableFrom =
    lastPage?.availableFrom ?? accessLog.data?.pages[0]?.availableFrom;
  const periodLabel =
    from === defaultRange.from && to === defaultRange.to
      ? "Últimos 7 días"
      : from && to
        ? `${formatDate(from)} · ${formatDate(to)}`
        : "Período filtrado";

  const columns: CursorTableColumn<AccessLogEntry>[] = [
    {
      key: "name",
      header: "Usuario",
      width: "1.5fr",
      render: (entry) => (
        <strong style={{ font: `600 12.5px ${tokens.font.ui}` }}>
          {entry.nameText || "Usuario no identificado"}
        </strong>
      ),
    },
    {
      key: "username",
      header: "Cuenta",
      width: "130px",
      render: (entry) => (
        <span style={accessLogStyles.accountText}>{entry.usernameText}</span>
      ),
    },
    {
      key: "occurredAt",
      header: "Fecha y hora",
      width: "190px",
      render: (entry) => (
        <span style={{ color: tokens.text.tertiary }}>
          {formatInstant(entry.occurredAt)}
        </span>
      ),
    },
    {
      key: "origin",
      header: "Origen",
      width: "1fr",
      render: (entry) => (
        <span style={{ color: tokens.text.tertiary }}>
          {entry.origin === "portal"
            ? "Portal administrativo"
            : "Validador QR · móvil"}
        </span>
      ),
    },
    {
      key: "result",
      header: "Resultado",
      // 150px fijos, como el prototipo (admin.jsx:3720). Fijo (no `auto`):
      // cada fila es su propia grilla y un `auto` desalineaba las filas con
      // badge largo (ver `accessLogStyles`).
      width: accessLogStyles.table.resultWidth,
      // El `Tooltip` está posicionado en absoluto: con el `overflow: hidden`
      // por defecto de la celda se recortaría contra su borde y no se vería.
      // El badge es `nowrap` y puede exceder la columna: con el recorte de la
      // celda el tooltip tampoco se vería, por eso `visible`; el contenedor de
      // la tabla recorta contra su borde, como en el prototipo.
      overflow: "visible",
      render: (entry) => {
        if (entry.result === "success") {
          return <span style={accessLogStyles.successBadge}>Exitoso</span>;
        }
        // El tooltip repite el texto completo de la celda, prefijo incluido:
        // antes decía solo el motivo, así que al pasar el mouse sobre
        // "Fallido · Credenciales inválidas" aparecía "Credenciales
        // inválidas" a secas.
        //
        // Revisión en vivo 2026-09-22, punto #16: el mecanismo era el
        // atributo `title` nativo del navegador — no se puede estilar, no
        // aparece con el foco de teclado y tarda ~1s. Pasa al `Tooltip`
        // propio del punto #10.
        //
        // Queda con el `placement` por defecto ("top"): el contenedor de
        // `CursorTable` tiene `overflowY: hidden`, así que un tooltip hacia
        // abajo en la última fila se recortaría contra el borde de la tabla;
        // hacia arriba siempre tiene el encabezado o la fila anterior de
        // colchón.
        const label = `Fallido · ${entry.reason ? reasonLabel[entry.reason] : "Sin detalle"}`;
        return (
          <Tooltip label={label}>
            <span style={accessLogStyles.failureBadge}>{label}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <SecurityHeader />
      <SecurityLogFilterBar
        ariaLabel="Filtros de bitácora"
        groupLabel="Resultado"
        chips={
          [
            ["all", "Todos"],
            ["success", "Exitoso"],
            ["failure", "Fallido"],
          ] as const
        }
        activeValue={result}
        onChipChange={setResult}
        trailingText={`${periodLabel} · ${lastPage?.meta.total ?? 0} registros`}
        trailingAriaLabel="Abrir filtros adicionales y disponibilidad de registros"
        onOpenAdvanced={() => setAdvancedFiltersOpen(true)}
      />

      {advancedFiltersOpen ? (
        // Revisión en vivo 2026-09-22, puntos #3/#4A/#15/#21: las tres
        // regiones del `Modal` y su encabezado común, armados por
        // `AdvancedFiltersModal` (`@portal/common`).
        <AdvancedFiltersModal
          eyebrow="Seguridad · Bitácora"
          label="Filtros adicionales de bitácora"
          width={560}
          onClose={() => setAdvancedFiltersOpen(false)}
          description={
            availableFrom
              ? `Registros disponibles desde ${formatDate(availableFrom)}.`
              : "Consultá por origen, usuario y rango de fechas."
          }
        >
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Origen
            <select
              aria-label="Filtrar por origen"
              value={origin}
              onChange={(event) =>
                setOrigin(event.target.value as OriginFilter)
              }
              style={securityLogFilterBarStyles.filterInput}
            >
              <option value="all">Origen · todos</option>
              <option value="portal">Portal administrativo</option>
              <option value="validator">Validador QR</option>
            </select>
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Usuario
            <input
              aria-label="Filtrar por identificador de usuario"
              placeholder="ID de usuario"
              value={staffUserId}
              onChange={(event) => setStaffUserId(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Desde
            <input
              aria-label="Fecha desde"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
          <label style={securityLogFilterBarStyles.advancedFilters.label}>
            Hasta
            <input
              aria-label="Fecha hasta"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              style={securityLogFilterBarStyles.filterInput}
            />
          </label>
        </AdvancedFiltersModal>
      ) : null}

      {accessLog.isLoading ? (
        <div style={{ padding: 22 }}>Cargando bitácora…</div>
      ) : accessLog.isError ? (
        <EmptyState kind="error" />
      ) : rows.length ? (
        <CursorTable
          columns={columns}
          rows={rows}
          rowKey={(entry) => entry.id}
          page={{
            nextCursor: lastPage?.meta.next ?? null,
            totalCount: lastPage?.meta.total ?? rows.length,
          }}
          onLoadMore={() => accessLog.fetchNextPage()}
          isLoadingMore={accessLog.isFetchingNextPage}
          minWidth={accessLogStyles.table.minWidth}
          headerPadding={accessLogStyles.table.headerPadding}
          rowPadding={accessLogStyles.table.rowPadding}
          rowFont={`400 11.5px ${tokens.font.ui}`}
          showFooter={Boolean(lastPage?.meta.next)}
        />
      ) : (
        <EmptyState
          kind={
            result !== "all" || origin !== "all" || staffUserId || from || to
              ? "no-results"
              : "no-data"
          }
        />
      )}
    </div>
  );
}
